import { useRef } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';
import { FilterDefinition } from 'src/uniform/contenttypes/filterDefinition';
import { SortDefinition } from 'src/uniform/contenttypes/sortDefinition';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { Button } from '@/libraries/design/components/button/src/button';

import { countActiveFilterOptions } from '../utils/helpers';

import { FILTER_DROPDOWN_DATA_ATTR, FilterControl } from './filter-control';
import { SearchField } from './search-field';
import { SortControl } from './sort-control';

import type { UniformFilterObject } from '../utils/types';

interface ToolbarProps {
	readonly filterDefs: FilterDefinition[];
	readonly sortDefs: SortDefinition[];
	readonly canSearch: boolean;
	readonly showSearchInput: boolean;
	readonly filterObject: UniformFilterObject;
	readonly searchQuery: string;
	readonly selectedSortId: string | undefined;
	readonly dropdownVisibility: Record<string, boolean>;
	readonly labelMaps: Record<string, Record<string, string>>;
	readonly onSearchChange: (value: string) => void;
	readonly onSearchSubmit: () => void;
	readonly onSearchClear: () => void;
	readonly onFilterButtonClick: (filterId: string) => void;
	readonly onOptionToggle: (filterId: string, optionId: string) => void;
	readonly onClearFilter: (filterId: string) => void;
	readonly onClearAllFilters: () => void;
	readonly onSortChange: (sortId: string) => void;
}

/**
 * Top row of the filterable-gallery surface: search field, the row of filter
 * controls (with a Clear-Filters action), and the sort control. Each section
 * is hidden when its corresponding definition list is empty.
 */
export const Toolbar = ({
	filterDefs,
	sortDefs,
	canSearch,
	showSearchInput,
	filterObject,
	searchQuery,
	selectedSortId,
	dropdownVisibility,
	labelMaps,
	onSearchChange,
	onSearchSubmit,
	onSearchClear,
	onFilterButtonClick,
	onOptionToggle,
	onClearFilter,
	onClearAllFilters,
	onSortChange,
}: ToolbarProps) => {
	const intl = useIntl();
	const filterRowRef = useRef<HTMLDivElement>(null);
	const handleClearAllFilters = () => {
		// The clear button disappears after reset; retain focus on a stable filter trigger.
		filterRowRef.current?.querySelector<HTMLButtonElement>('button[aria-expanded]')?.focus();
		onClearAllFilters();
	};
	const hasActiveFilters = filterDefs.some(
		(filter) => countActiveFilterOptions(filter.id, filterObject) > 0,
	);

	return (
		<section css={toolbarSectionStyle}>
			<div css={[toolbarRowStyle, filterDefs.length > 1 && multiFilterToolbarRowStyle]}>
				{showSearchInput && canSearch && (
					<div css={searchContainerStyle}>
						<SearchField
							value={searchQuery}
							onChange={onSearchChange}
							onSubmit={onSearchSubmit}
							onClear={onSearchClear}
						/>
					</div>
				)}

				{filterDefs.length > 0 && (
					<div ref={filterRowRef} css={filterButtonRowStyle}>
						{filterDefs.map((filter) => (
							<div
								key={filter.id}
								css={filterWrapperStyle}
								{...{ [FILTER_DROPDOWN_DATA_ATTR]: '' }}
							>
								<FilterControl
									filter={filter}
									activeCount={countActiveFilterOptions(filter.id, filterObject)}
									isOpen={!!dropdownVisibility[filter.id]}
									subFilter={filterObject[filter.id]}
									labelMap={labelMaps[filter.id]}
									onButtonClick={() => onFilterButtonClick(filter.id)}
									onOptionToggle={(optionId) => onOptionToggle(filter.id, optionId)}
									onSelectAll={() => onClearFilter(filter.id)}
								/>
							</div>
						))}

						{hasActiveFilters && (
							<Button
								variant="primary_alt"
								size="small"
								onClick={handleClearAllFilters}
								className="body-md"
								css={clearFiltersButtonStyle}
								dataEventProps={{
									'data-product-key': 'uniform',
									'data-event': 'clicked',
									'data-event-component': 'button',
									'data-event-container': 'searchWithFilters',
									'data-label': 'Clear Filters',
									'data-label-english': 'Clear Filters',
								}}
							>
								{intl.formatMessage({
									id: 'uniformFilterableGallery.clearFilters',
									defaultMessage: 'Clear Filters',
								})}
							</Button>
						)}
					</div>
				)}

				{sortDefs.length > 0 && (
					<div css={sortRowStyle}>
						<SortControl sorts={sortDefs} selectedSortId={selectedSortId} onChange={onSortChange} />
					</div>
				)}
			</div>
		</section>
	);
};

const toolbarSectionStyle = css({
	display: 'flex',
	flexDirection: 'column',
	position: 'relative',
});

const toolbarRowStyle = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'flex-start',
	justifyContent: 'space-between',
	flexWrap: 'nowrap',
	padding: '48px 0 16px',
	gap: token('space.200'),
	// A 160px filter plus a 280px search no longer fit side by side below `sm`, so stack them at the start.
	[media.below.sm]: {
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.300'),
	},
	[media.below.xs]: {
		padding: '48px 0 24px',
	},
});

const multiFilterToolbarRowStyle = css({
	padding: '16px 0',
	[media.below.xs]: {
		padding: '48px 0 24px',
	},
});

const searchContainerStyle = css({
	order: 3,
	flex: '0 1 420px',
	minWidth: '280px',
	display: 'flex',
	justifyContent: 'flex-end',
	// space-between alone leaves a lone search field at the start; auto margin pins it to the grid end.
	marginInlineStart: 'auto',
	[media.below.sm]: {
		order: 2,
		marginInlineStart: 0,
		justifyContent: 'flex-start',
		flex: 'none',
		width: '100%',
		maxWidth: 'none',
		minWidth: 0,
	},
});

const filterButtonRowStyle = css({
	order: 1,
	flex: '1 1 auto',
	minWidth: 0,
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'wrap',
	gap: token('space.200'),
	position: 'relative',
	[media.below.sm]: {
		flex: 'none',
		width: '100%',
	},
	[media.below.xs]: {
		flexDirection: 'column',
	},
});

const filterWrapperStyle = css({
	// Unstacked, the pill hugs its label (Figma symbol is 160px wide) and never grows past 368px.
	flex: '0 0 auto',
	minWidth: '160px',
	maxWidth: '368px',
	[media.below.sm]: {
		flex: 'none',
		width: '100%',
		minWidth: 0,
		maxWidth: 'none',
	},
});

const clearFiltersButtonStyle = css({
	whiteSpace: 'nowrap',
});

const sortRowStyle = css({
	marginBlockStart: token('space.150'),
	order: 2,
	display: 'flex',
	justifyContent: 'flex-end',
});
